import { useState, useRef, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { Clock, RefreshCw } from 'lucide-react';
import { useAppDispatch } from '@/state/store';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import {
  useComponentLDetail,
  useScanInventoryFile,
} from '@/components/smartai/api/getComponentDetails';
import type { PollerState } from '@/components/smartai/api/getComponentDetails';
import { useFileContent } from '@/components/smartai/api/getFileContent';
import { getComponentList } from '@/components/smartai/api/getComponentList';
import type { OnboardedData } from '@/components/smartai/api/getComponentList';
import { userSelectedComponent } from '@/state/slices/userSelectedComponentSlice';
import { api } from '@/shared/axios-client';
import CodeEditor, { type StructureSpan } from '@/components/common/CodeEditor';
import type { RootState } from '@/state/store';
import { useSelectedComponent } from '@/hooks/useSelectedComponent';
import { getWebhookConfigQueryOptions } from '@/components/smartai/api/webhookConfigApi';
import ComponentNotFound from '@/hooks/ComponentNotFound';
import { extractAllElements } from '@/utils/extractElements';
import {
  groupInventoryProjects,
  languageCaption,
  stripProjectPrefix,
  type InventoryLanguage,
} from '@/utils/inventoryWorkspace';
import OnboardingPageHeader from '@/components/smartai/ComponentOnboarding/OnboardingPageHeader';
import TechStackPanel from '@/components/smartai/ComponentsList/TechStackPanel';
import TechnologySymbol from '@/components/smartai/ComponentsList/TechnologySymbol';
import { onboardingPageClass } from '@/components/smartai/ComponentOnboarding/onboarding-layout';
import { INVENTORY_EXPLORER_HEIGHT } from '@/components/smartai/ComponentsList/components-page-layout';
import InventorySkeleton, {
  InventoryCodePaneSkeleton,
} from './InventorySkeleton';
import { cn } from '@/lib/utils';

interface ASTNode {
  type: 'program' | 'class' | 'method' | 'function' | 'branch' | 'loop';
  name?: string;
  startLine?: number;
  endLine?: number;
  lineStart?: number;
  lineEnd?: number;
  executableLines: number[];
  nonExecutableLines: number[];
  children: ASTNode[];
}
interface AST {
  type: 'program';
  children: ASTNode[];
}
interface FileDetail {
  fileName: string;
  filePath: string;
  totalLines: number;
  totalExecutableLines: number;
  totalNonExecutableLines: number;
  totalClasses: number;
  totalMethods: number;
  totalFunctions: number;
  totalBranches: number;
  totalLoops: number;
  error: string | null;
  abstractSyntaxTree?: AST;
}
interface Pagination {
  totalFiles: number;
  returnedFiles: number;
  offset: number;
  limit: number;
}
interface ScanReport {
  scanId: string;
  commitId?: string;
  totalFiles: number;
  totalLines: number;
  totalMethods: number;
  totalFunctions: number;
  totalClasses: number;
  totalNonExecutableLines: number;
  totalExecutableLines: number;
  totalBranches: number;
  totalLoops: number;
  pagination: Pagination;
  fileDetails: FileDetail[];
  fileIndex?: FileDetail[];
}

type FlatASTNode = Omit<ASTNode, 'children'>;

function getEntityName(type: ASTNode['type']): string {
  switch (type) {
    case 'program':
      return 'Program';
    case 'class':
      return 'Class';
    case 'method':
      return 'Method';
    case 'function':
      return 'Function';
    case 'branch':
      return 'Branch';
    case 'loop':
      return 'Loop';
    default:
      return type;
  }
}

const formatCount = (value: number | string | undefined): string => {
  if (value === undefined || value === null || value === '-') return '—';
  const num = typeof value === 'number' ? value : Number(value);
  if (Number.isNaN(num)) return String(value);
  return num.toLocaleString();
};

function technologyForProject(
  language: InventoryLanguage,
  files: { filePath?: string; fileName?: string }[],
): string {
  if (language === 'python') return 'Python';
  if (language === 'javascript') return 'JavaScript';
  if (language === 'csharp') return 'C#';
  if (language === 'typescript') {
    const hasTsx = files.some(file =>
      (file.filePath || file.fileName || '').toLowerCase().endsWith('.tsx'),
    );
    return hasTsx ? 'React' : 'TypeScript';
  }
  return 'TypeScript';
}

function ProjectFilterChip({
  selected,
  technologies,
  title,
  caption,
  onClick,
}: {
  selected?: boolean;
  technologies: string[];
  title: string;
  caption: string;
  onClick?: () => void;
}) {
  const className = cn(
    'inline-flex items-center gap-2.5 rounded-[14px] border border-solid px-3 py-2 text-left',
    selected
      ? 'border-[var(--inv-accent)] bg-[var(--inv-accent-soft-2)] text-[var(--inv-text)] shadow-[0_0_0_1px_var(--inv-accent)]'
      : 'border-[var(--inv-border)] bg-[var(--inv-surface)] text-[var(--inv-text)]',
  );
  const inner = (
    <>
      {technologies.map(tech => (
        <TechnologySymbol key={tech} technology={tech} size="md" />
      ))}
      <span>
        <span className="block text-[0.875rem] font-semibold leading-tight">
          {title}
        </span>
        <span className="mt-0.5 block text-[0.75rem] leading-tight text-[var(--inv-text-secondary)]">
          {caption}
        </span>
      </span>
    </>
  );
  if (!onClick) {
    return <span className={className}>{inner}</span>;
  }
  return (
    <button type="button" role="tab" aria-selected={selected} className={className} onClick={onClick}>
      {inner}
    </button>
  );
}

function aggregateFileMetrics(files: FileDetail[]) {
  return files.reduce(
    (acc, file) => ({
      totalFiles: acc.totalFiles + 1,
      totalLines: acc.totalLines + (file.totalLines || 0),
      totalExecutableLines:
        acc.totalExecutableLines + (file.totalExecutableLines || 0),
      totalNonExecutableLines:
        acc.totalNonExecutableLines + (file.totalNonExecutableLines || 0),
      totalClasses: acc.totalClasses + (file.totalClasses || 0),
      totalMethods: acc.totalMethods + (file.totalMethods || 0),
      totalFunctions: acc.totalFunctions + (file.totalFunctions || 0),
      totalBranches: acc.totalBranches + (file.totalBranches || 0),
      totalLoops: acc.totalLoops + (file.totalLoops || 0),
    }),
    {
      totalFiles: 0,
      totalLines: 0,
      totalExecutableLines: 0,
      totalNonExecutableLines: 0,
      totalClasses: 0,
      totalMethods: 0,
      totalFunctions: 0,
      totalBranches: 0,
      totalLoops: 0,
    },
  );
}

function shortCommitId(commitId?: string): string {
  const value = (commitId || '').trim();
  if (!value) return '';
  return value.length > 8 ? value.slice(0, 8) : value;
}

function formatCodeShare(part: number, whole: number): string {
  if (!whole || !Number.isFinite(part) || !Number.isFinite(whole)) return '—';
  const pct = (part / whole) * 100;
  const rounded = pct >= 10 || pct === 0 ? pct.toFixed(0) : pct.toFixed(1);
  return `${rounded}% of code`;
}

function MetricCard({
  label,
  value,
  hint,
  stripClass,
  valueClass,
  barClass,
  barPercent,
}: {
  label: string;
  value: string;
  hint: string;
  stripClass: string;
  valueClass?: string;
  barClass?: string;
  barPercent?: number;
}) {
  const showBar =
    barClass != null && barPercent != null && Number.isFinite(barPercent);
  return (
    <div className="overflow-hidden rounded-[14px] border border-solid border-[var(--inv-border)] bg-[var(--inv-surface)] shadow-[var(--inv-shadow,0_1px_2px_rgba(43,33,24,0.04))]">
      <span className={cn('block h-1.5', stripClass)} aria-hidden="true" />
      <div className="px-4 py-3">
        <span className="mb-2 block text-[0.65rem] font-bold uppercase tracking-[0.12em] text-[var(--inv-text-muted)]">
          {label}
        </span>
        <span
          className={cn(
            'block font-serif text-[1.625rem] font-medium leading-none tabular-nums text-[var(--inv-text)]',
            valueClass,
          )}>
          {value}
        </span>
        <span className="mt-2 block text-[0.8125rem] text-[var(--inv-text-secondary)]">
          {hint}
        </span>
        {showBar ? (
          <div
            className="mt-3 h-1.5 overflow-hidden rounded-full bg-[var(--inv-surface-muted)]"
            aria-hidden="true">
            <div
              className={cn('h-full rounded-full', barClass)}
              style={{ width: `${Math.min(100, Math.max(0, barPercent))}%` }}
            />
          </div>
        ) : null}
      </div>
    </div>
  );
}

function InventoryMetricsOverview({
  report,
  filesCaption,
}: {
  report: ScanReport;
  filesCaption: string;
}) {
  const methods = report.totalMethods ?? 0;
  const functions = report.totalFunctions ?? 0;
  const executable = report.totalExecutableLines ?? 0;
  const boilerplate = report.totalNonExecutableLines ?? 0;
  const classified = executable + boilerplate || report.totalLines || 0;
  const executableShare = classified ? (executable / classified) * 100 : 0;
  const boilerplateShare = classified ? (boilerplate / classified) * 100 : 0;

  const secondary: [string, number | string | undefined, string][] = [
    ['Classes', report.totalClasses, 'bg-[#7c3aed]'],
    ['Methods', report.totalMethods, 'bg-[#2563eb]'],
    ['Functions', report.totalFunctions, 'bg-[#0d9488]'],
    ['Branches', report.totalBranches, 'bg-[#ea580c]'],
    ['Loops', report.totalLoops, 'bg-[#db2777]'],
  ];

  return (
    <section className="mb-6 flex flex-col gap-3" aria-label="Scan summary">
      <div className="grid grid-cols-1 gap-3 min-[720px]:grid-cols-2 min-[1100px]:grid-cols-3 min-[1536px]:grid-cols-5">
        <MetricCard
          label="Files"
          value={formatCount(report.totalFiles)}
          hint={filesCaption}
          stripClass="bg-[var(--inv-accent)]"
        />
        <MetricCard
          label="Lines"
          value={formatCount(report.totalLines)}
          hint="excluding dependencies"
          stripClass="bg-[var(--inv-accent)]"
        />
        <MetricCard
          label="Executable"
          value={formatCount(executable)}
          hint={formatCodeShare(executable, classified)}
          stripClass="bg-[#16a34a]"
          valueClass="text-[#15803d]"
          barClass="bg-[#16a34a]"
          barPercent={executableShare}
        />
        <MetricCard
          label="Boilerplate"
          value={formatCount(boilerplate)}
          hint={formatCodeShare(boilerplate, classified)}
          stripClass="bg-[var(--inv-accent)]"
          valueClass="text-red-500"
          barClass="bg-red-500"
          barPercent={boilerplateShare}
        />
        <MetricCard
          label="Methods and functions"
          value={formatCount(methods + functions)}
          hint={`${formatCount(methods)} methods · ${formatCount(functions)} standalone functions`}
          stripClass="bg-[var(--inv-accent)]"
        />
      </div>
      <div className="grid grid-cols-2 gap-2.5 min-[900px]:grid-cols-5">
        {secondary.map(([label, value, dot]) => (
          <div
            key={label}
            className="overflow-hidden rounded-[14px] border border-solid border-[var(--inv-border)] bg-[var(--inv-surface)] shadow-[var(--inv-shadow,0_1px_2px_rgba(43,33,24,0.04))]">
            <span className={cn('block h-1', dot)} aria-hidden="true" />
            <div className="flex items-center gap-2.5 px-3.5 py-3">
              <span className={cn('h-2 w-2 shrink-0 rounded-[2px]', dot)} />
              <div className="min-w-0">
                <span className="block text-[0.62rem] font-bold uppercase tracking-[0.1em] text-[var(--inv-text-muted)]">
                  {label}
                </span>
                <span className="text-[1.125rem] font-semibold leading-tight tabular-nums text-[var(--inv-text)]">
                  {formatCount(value)}
                </span>
              </div>
            </div>
          </div>
        ))}
      </div>
      <h2 className="sr-only">Code elements</h2>
    </section>
  );
}

function StructureInFile({
  fileName,
  entities,
}: {
  fileName: string;
  entities: FlatASTNode[];
}) {
  const color: Record<string, string> = {
    class: 'bg-[#7c3aed]',
    method: 'bg-[#2563eb]',
    function: 'bg-[#0d9488]',
    branch: 'bg-[#ea580c]',
    loop: 'bg-[#db2777]',
  };

  const sortedEntities = useMemo(
    () => sortStructureEntities(entities),
    [entities],
  );

  return (
    <div className="flex h-[min(11rem,18vh)] shrink-0 flex-col overflow-hidden rounded-xl border border-solid border-[var(--inv-border)] bg-[var(--inv-surface)]">
      <div className="flex shrink-0 items-center justify-between border-0 border-b border-solid border-[var(--inv-border)] bg-[var(--inv-surface-muted)] px-4 py-2.5">
        <span className="text-[0.65rem] font-bold uppercase tracking-[0.1em] text-[var(--inv-text-muted)]">
          Structure in this file
        </span>
        <span className="text-[0.75rem] tabular-nums text-[var(--inv-text-secondary)]">
          {sortedEntities.length} element{sortedEntities.length === 1 ? '' : 's'}
        </span>
      </div>
      {sortedEntities.length === 0 ? (
        <p className="m-0 flex flex-1 items-center px-4 text-sm text-[var(--inv-text-muted)]">
          No entity details available for {fileName}.
        </p>
      ) : (
        <ul className="m-0 flex-1 list-none overflow-auto overscroll-contain p-0">
          {sortedEntities.map((row, index) => {
            const start = row.lineStart ?? row.startLine;
            const end = row.lineEnd ?? row.endLine;
            const kind = railKind(String(row.type || '').toLowerCase());
            return (
              <li
                key={`${row.type}-${row.name ?? ''}-${start ?? index}`}
                className="flex items-center justify-between gap-3 border-0 border-b border-solid border-[var(--inv-border-soft)] px-4 py-2 last:border-b-0">
                <span className="flex min-w-0 items-center gap-2">
                  <span
                    className={cn(
                      'h-2 w-2 shrink-0 rounded-[2px]',
                      color[kind ?? ''] ?? 'bg-[var(--inv-text-muted)]',
                    )}
                  />
                  <span className="truncate font-mono text-[0.8125rem] text-[var(--inv-text)]">
                    {row.name || getEntityName(row.type)}
                  </span>
                </span>
                <span className="shrink-0 tabular-nums text-[0.75rem] text-[var(--inv-text-muted)]">
                  {start != null && end != null ? `${start}–${end}` : '—'}
                </span>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

function railKind(type: string): StructureSpan['type'] | null {
  if (
    type === 'class' ||
    type === 'class_declaration' ||
    type === 'interface_declaration' ||
    type === 'enum_declaration' ||
    type === 'struct_declaration' ||
    type === 'record_declaration'
  ) {
    return 'class';
  }
  if (
    type === 'method' ||
    type === 'method_declaration' ||
    type === 'method_definition' ||
    type === 'constructor' ||
    type === 'constructor_declaration'
  ) {
    return 'method';
  }
  if (
    type === 'function' ||
    type === 'function_declaration' ||
    type === 'function_expression' ||
    type === 'arrow_function' ||
    type === 'lambda_expression' ||
    type === 'generator_function' ||
    type === 'local_function_statement' ||
    type === 'anonymous_method_expression'
  ) {
    return 'function';
  }
  if (
    type === 'branch' ||
    type === 'if_branch' ||
    type === 'else_if_branch' ||
    type === 'else_branch' ||
    type === 'switch_statement' ||
    type === 'switch_expression' ||
    type === 'ternary_expression' ||
    type === 'conditional_expression'
  ) {
    return 'branch';
  }
  if (
    type === 'loop' ||
    type === 'for_statement' ||
    type === 'for_in_statement' ||
    type === 'for_of_statement' ||
    type === 'for_each_statement' ||
    type === 'while_statement' ||
    type === 'do_statement'
  ) {
    return 'loop';
  }
  return null;
}

/** Display order for Structure in this file: class->methods → function → branches → loops. */
const STRUCTURE_KIND_ORDER: Record<NonNullable<ReturnType<typeof railKind>>, number> = {
  class: 0,
  method: 1,
  function: 2,
  branch: 3,
  loop: 4,
};

function sortStructureEntities(entities: FlatASTNode[]): FlatASTNode[] {
  return [...entities].sort((a, b) => {
    const kindA = railKind(String(a.type || '').toLowerCase());
    const kindB = railKind(String(b.type || '').toLowerCase());
    const orderA = kindA != null ? STRUCTURE_KIND_ORDER[kindA] : 999;
    const orderB = kindB != null ? STRUCTURE_KIND_ORDER[kindB] : 999;
    if (orderA !== orderB) return orderA - orderB;
    const startA = Number(a.lineStart ?? a.startLine ?? 0);
    const startB = Number(b.lineStart ?? b.startLine ?? 0);
    return startA - startB;
  });
}

function collectStructure(root?: AST | ASTNode[] | null): StructureSpan[] {
  const spans: StructureSpan[] = [];
  const walk = (list?: ASTNode[]) => {
    for (const node of list ?? []) {
      const type = String(node.type || '').toLowerCase();
      if (type === 'program') {
        walk(node.children);
        continue;
      }
      const kind = railKind(type);
      const start = Number(node.lineStart || node.startLine || 0);
      const end = Number(node.lineEnd || node.endLine || 0);
      if (kind && start > 0 && end >= start) {
        spans.push({
          type: kind,
          startLine: start,
          endLine: end,
          depth: 0,
        });
      }
      walk(node.children);
    }
  };
  if (Array.isArray(root)) walk(root);
  else walk(root?.children);
  return spans.map(span => ({
    ...span,
    depth: spans.filter(
      other =>
        other !== span &&
        other.startLine <= span.startLine &&
        other.endLine >= span.endLine &&
        other.endLine - other.startLine > span.endLine - span.startLine,
    ).length,
  }));
}

function InventoryCodePane({
  file,
  repoUrl,
  branchName,
  scanId,
  pat,
}: {
  file: FileDetail;
  repoUrl: string;
  branchName: string;
  scanId: string;
  pat: string;
}) {
  const filePath = file.filePath || file.fileName;
  const { data, isPending, isError } = useFileContent({
    repoUrl,
    branchName,
    filePath,
    scanId,
    pat,
  });

  return (
    <div className="flex h-[min(62vh,40rem)] shrink-0 flex-col overflow-hidden rounded-xl border border-solid border-[var(--inv-border)] bg-black min-[960px]:h-0 min-[960px]:min-h-[20rem] min-[960px]:flex-1">
      <div className="flex items-center justify-between gap-3 border-0 border-b border-solid border-[#30363d] px-3 py-2">
        <code className="truncate font-mono text-[0.75rem] text-[#e6edf3]">
          {filePath}
        </code>
        <span className="shrink-0 text-[0.7rem] tabular-nums text-[#8b949e]">
          {file.totalLines} lines · {file.totalExecutableLines} executable
          {file.totalMethods ? ` · ${file.totalMethods} methods` : ''}
        </span>
      </div>
      {isPending ? <InventoryCodePaneSkeleton /> : null}
      {isError ? (
        <p className="m-0 p-4 text-sm text-[#f85149]">Failed to load file content.</p>
      ) : null}
      {data?.data ? (
        <div className="relative min-h-0 flex-1 overflow-hidden">
          <div className="absolute inset-0">
            <CodeEditor
              codeFromApi={data.data.content}
              language={data.data.language ?? filePath}
              executable={data.data.lineClassification?.executable ?? []}
              nonExecutable={data.data.lineClassification?.nonExecutable ?? []}
              structure={collectStructure(file.abstractSyntaxTree)}
              structureMode
              instanceId={`inventory-${filePath}`}
              height="100%"
            />
          </div>
        </div>
      ) : null}
    </div>
  );
}

const ComponentInventoryView = () => {
  const { ready, isLoading, notFound } = useSelectedComponent();

  const selectedComponent = useSelector(
    (state: RootState) => state.userSelectedComponentSlice,
  );

  const dispatch = useAppDispatch();
  const queryClient = useQueryClient();
  const { data: webhookConfig } = useQuery(
    getWebhookConfigQueryOptions(selectedComponent.dbRepoId),
  );

  const [selectedFile, setSelectedFile] = useState<FileDetail | null>(null);
  const [refreshLoading, setRefreshLoading] = useState(false);
  const pollIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const pollTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const [accumulatedFiles, setAccumulatedFiles] = useState<FileDetail[]>([]);
  const [fileIndex, setFileIndex] = useState<FileDetail[]>([]);
  const [totalFiles, setTotalFiles] = useState(0);
  const [activeProjectId, setActiveProjectId] = useState('all');

  const projects = useMemo(
    () =>
      groupInventoryProjects(
        fileIndex.length ? fileIndex : accumulatedFiles,
        selectedComponent.repoName || 'Repository',
      ),
    [fileIndex, accumulatedFiles, selectedComponent.repoName],
  );
  const isMonorepo = projects.length > 1;
  const listedFiles = useMemo(() => {
    if (!isMonorepo || activeProjectId === 'all') {
      return projects.flatMap(project => project.files);
    }
    return projects.find(project => project.id === activeProjectId)?.files ?? [];
  }, [isMonorepo, activeProjectId, projects]);

  useEffect(() => {
    setAccumulatedFiles([]);
    setFileIndex([]);
    setTotalFiles(0);
    setActiveProjectId('all');
  }, [selectedComponent.scanId]);

  useEffect(() => {
    setSelectedFile(prev => {
      if (!listedFiles.length) return null;
      const id = prev?.filePath || prev?.fileName;
      const next =
        id && listedFiles.some(file => (file.filePath || file.fileName) === id)
          ? prev
          : listedFiles[0];
      const nextId = next.filePath || next.fileName;
      if (next.abstractSyntaxTree) return next;
      return (
        accumulatedFiles.find(
          file => (file.filePath || file.fileName) === nextId,
        ) ?? next
      );
    });
  }, [listedFiles, accumulatedFiles]);

  useEffect(() => {
    return () => {
      if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);
      if (pollTimeoutRef.current) clearTimeout(pollTimeoutRef.current);
    };
  }, []);

  const stopPolling = () => {
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }
    if (pollTimeoutRef.current) {
      clearTimeout(pollTimeoutRef.current);
      pollTimeoutRef.current = null;
    }
    setRefreshLoading(false);
  };

  const fetchOnboarded = async () => {
    try {
      const response = await getComponentList();
      if (response?.data) {
        const updatedRepo = response.data.find(
          (repo: OnboardedData) => repo.repoId === selectedComponent.repoId,
        );
        if (updatedRepo && updatedRepo.scanId !== selectedComponent.scanId) {
          stopPolling();
          dispatch(userSelectedComponent(updatedRepo));
          queryClient.invalidateQueries({ queryKey: ['component-list'] });
        }
      }
    } catch (error) {
      console.error('Error polling onboarded:', error);
    }
  };

  const handleRefreshInventory = () => {
    if (webhookEnabled) {
      return;
    }

    setRefreshLoading(true);
    void api.post(
      '/api/poll/trigger',
      {
        repoId: selectedComponent?.dbRepoId,
      },
      {
        headers: { 'Requester-Token': selectedComponent.pat },
      },
    );
    pollIntervalRef.current = setInterval(() => {
      void fetchOnboarded();
    }, 3000);
    pollTimeoutRef.current = setTimeout(() => {
      stopPolling();
    }, 5 * 60 * 1000);
  };

  const {
    isPending,
    isError,
    data: displayData,
    error,
  } = useComponentLDetail(selectedComponent.scanId);

  const selectedPath = selectedFile?.filePath || selectedFile?.fileName || '';
  const { data: selectedFileAst } = useScanInventoryFile(
    selectedComponent.scanId,
    selectedPath,
    Boolean(selectedPath && !selectedFile?.abstractSyntaxTree),
  );

  useEffect(() => {
    const detail = selectedFileAst?.data?.fileDetails?.[0];
    if (!detail?.abstractSyntaxTree) return;
    setSelectedFile(prev => {
      if (!prev) return prev;
      const prevPath = (prev.filePath || prev.fileName || '').replace(/\\/g, '/');
      const detailPath = (detail.filePath || detail.fileName || '').replace(/\\/g, '/');
      if (prevPath !== detailPath) {
        return prev;
      }
      if (prev.abstractSyntaxTree) return prev;
      return { ...prev, abstractSyntaxTree: detail.abstractSyntaxTree };
    });
    setAccumulatedFiles(prev => {
      const detailPath = (detail.filePath || detail.fileName || '').replace(/\\/g, '/');
      const idx = prev.findIndex(
        file => (file.filePath || file.fileName || '').replace(/\\/g, '/') === detailPath,
      );
      if (idx === -1) return [...prev, detail];
      if (prev[idx].abstractSyntaxTree) return prev;
      const next = [...prev];
      next[idx] = { ...next[idx], abstractSyntaxTree: detail.abstractSyntaxTree };
      return next;
    });
  }, [selectedFileAst]);

  useEffect(() => {
    if (!displayData?.data) return;
    setTotalFiles(displayData.data.pagination.totalFiles);
    setAccumulatedFiles(displayData.data.fileDetails);
    if (displayData.data.fileIndex?.length) {
      setFileIndex(displayData.data.fileIndex as FileDetail[]);
    }
  }, [displayData]);

  if (isLoading || (!ready && !notFound) || isPending) {
    return <InventorySkeleton />;
  }
  if (notFound) return <ComponentNotFound />;
  if (
    typeof selectedComponent.scanId === 'undefined' ||
    selectedComponent.scanId === ''
  ) {
    return (
      <div className={onboardingPageClass}>
        No scan ID available. Please select a component.
      </div>
    );
  }
  if (isError) {
    return (
      <div className={onboardingPageClass}>
        <span className="text-sm text-[#b42318]">Error: {error.message}</span>
      </div>
    );
  }

  const pollerState: PollerState | undefined = displayData?.data?.pollerState;
  const explorerFile = selectedFile;
  const structureEntities = explorerFile?.abstractSyntaxTree
    ? extractAllElements(explorerFile.abstractSyntaxTree)
    : [];
  const commitShort = shortCommitId(displayData?.data?.commitId);
  const scanSubtitle = [
    isMonorepo
      ? `${projects.length} projects`
      : selectedComponent.repoName || '1 project',
    commitShort
      ? `scanned at commit ${commitShort}`
      : selectedComponent.lastScanAt
        ? `scanned ${new Date(selectedComponent.lastScanAt).toLocaleString()}`
        : 'latest scan',
    'nothing here depends on a test run',
  ].join(' · ');
  const overviewReport =
    displayData?.data && isMonorepo && activeProjectId !== 'all'
      ? { ...displayData.data, ...aggregateFileMetrics(listedFiles) }
      : displayData?.data;
  const activeProjectLabel =
    projects.find(project => project.id === activeProjectId)?.label ?? '';
  const filesCaption =
    isMonorepo && activeProjectId !== 'all'
      ? `in ${activeProjectLabel}`
      : `across ${isMonorepo ? projects.length : 1} project${
          isMonorepo && projects.length !== 1 ? 's' : ''
        }`;
  const webhookEnabled = Boolean(
    webhookConfig?.isWebhookEnabled ?? selectedComponent.isWebhookEnabled,
  );

  const headerActions = (
    <div className="flex w-full flex-col items-end gap-2.5 max-[768px]:items-stretch">
      {webhookEnabled ? (
        <span className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-full border border-solid border-[var(--inv-border)] bg-[var(--inv-surface)] px-2.5 py-1.5 text-[0.6875rem] font-semibold tracking-wide text-[var(--inv-text-secondary)]">
          Webhook enabled — inventory polling disabled
        </span>
      ) : pollerState ? (
        <div className="flex flex-wrap justify-end gap-2 max-[768px]:justify-start">
          <span className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-full border border-solid border-[var(--inv-border)] bg-[var(--inv-surface)] px-2.5 py-1.5 text-[0.6875rem] font-semibold tracking-wide text-[var(--inv-text-secondary)]">
            <Clock
              className="h-3 w-3 text-[var(--inv-accent)]"
              aria-hidden="true"
            />
            Scheduled:{' '}
            {new Date(`${pollerState.nextScheduledPoll}Z`).toLocaleString()}
          </span>
          <span className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-full border border-solid border-[var(--inv-border)] bg-[var(--inv-surface)] px-2.5 py-1.5 text-[0.6875rem] font-semibold tracking-wide text-[var(--inv-text-secondary)]">
            <RefreshCw
              className="h-3 w-3 text-[var(--inv-accent)]"
              aria-hidden="true"
            />
            Last polled:{' '}
            {new Date(`${pollerState.lastCodePolledTime}Z`).toLocaleString()}
          </span>
        </div>
      ) : null}
      {!webhookEnabled ? (
        <Button
          variant="secondary"
          onClick={handleRefreshInventory}
          disabled={refreshLoading}>
          <RefreshCw className="h-4 w-4" aria-hidden="true" />
          {refreshLoading ? 'Refreshing…' : 'Refresh inventory'}
        </Button>
      ) : null}
    </div>
  );

  return (
    <div className={onboardingPageClass}>
      <OnboardingPageHeader
        title="Inventory"
        subtitle={scanSubtitle}
        actions={headerActions}
        className="mb-5"
      />

      <div className="mb-4 flex flex-wrap items-center gap-2.5" role="tablist" aria-label="Projects">
        <span className="mr-1 text-[0.65rem] font-bold uppercase tracking-[0.14em] text-[var(--inv-text-muted)]">
          Showing
        </span>
        {isMonorepo ? (
          <>
            <ProjectFilterChip
              selected={activeProjectId === 'all'}
              technologies={[
                ...new Set(
                  projects.map(project =>
                    technologyForProject(project.language, project.files),
                  ),
                ),
              ]}
              title="Total"
              caption={`${totalFiles} files`}
              onClick={() => setActiveProjectId('all')}
            />
            {projects.map(project => (
              <ProjectFilterChip
                key={project.id}
                selected={activeProjectId === project.id}
                technologies={[
                  technologyForProject(project.language, project.files),
                ]}
                title={project.label}
                caption={`${languageCaption(project.language, project.files)} · ${project.files.length} files`}
                onClick={() => setActiveProjectId(project.id)}
              />
            ))}
          </>
        ) : (
          <ProjectFilterChip
            technologies={[
              technologyForProject(
                projects[0]?.language ?? 'mixed',
                projects[0]?.files ?? accumulatedFiles,
              ),
            ]}
            title={projects[0]?.label ?? selectedComponent.repoName}
            caption={`${languageCaption(
              projects[0]?.language ?? 'mixed',
              projects[0]?.files ?? accumulatedFiles,
            )} · ${fileIndex.length || totalFiles || accumulatedFiles.length} files`}
          />
        )}
      </div>

      {overviewReport ? (
        <InventoryMetricsOverview
          report={overviewReport}
          filesCaption={filesCaption}
        />
      ) : null}

      <section className="mt-2" aria-label="File-level breakdown">
        <div className="mb-3.5 flex flex-wrap items-baseline justify-between gap-4">
          <h2 className="m-0 font-serif text-lg font-semibold text-[var(--inv-text)]">
            File-level breakdown
          </h2>
          <span className="text-[0.8125rem] tabular-nums text-[var(--inv-text-secondary)]">
            Showing {listedFiles.length.toLocaleString()} of{' '}
            {totalFiles.toLocaleString()} files
          </span>
        </div>

        <div
          className={cn(
            'mb-5 grid grid-cols-1 gap-4 min-[960px]:grid-cols-[minmax(14rem,20rem)_minmax(0,1fr)]',
            INVENTORY_EXPLORER_HEIGHT,
          )}>
          <aside className="overflow-auto rounded-xl border border-solid border-[var(--inv-border)] bg-[var(--inv-surface)] min-[960px]:h-full min-[960px]:max-h-full">
            <div className="sticky top-0 z-[1] flex items-center justify-between border-0 border-b border-solid border-[var(--inv-border)] bg-[var(--inv-surface-muted)] px-3 py-2">
              <span className="text-[0.65rem] font-bold uppercase tracking-[0.1em] text-[var(--inv-text-muted)]">
                Files
              </span>
              <span className="text-[0.7rem] tabular-nums text-[var(--inv-text-secondary)]">
                {listedFiles.length} shown
              </span>
            </div>
            {(isMonorepo && activeProjectId === 'all' ? projects : projects.filter(p => !isMonorepo || p.id === activeProjectId)).map(
              project => (
                <div key={project.id}>
                  {isMonorepo ? (
                    <div className="flex items-center gap-2 px-3 py-1.5 text-[0.75rem] font-semibold text-[var(--inv-text-secondary)]">
                      <TechnologySymbol
                        technology={technologyForProject(
                          project.language,
                          project.files,
                        )}
                        size="sm"
                      />
                      {project.label}
                    </div>
                  ) : null}
                  <ul className="m-0 list-none p-0">
                    {project.files.map(file => {
                      const path = file.filePath || file.fileName;
                      const active =
                        (explorerFile?.filePath || explorerFile?.fileName) === path;
                      return (
                        <li key={path}>
                          <button
                            type="button"
                            className={cn(
                              'flex w-full items-baseline justify-between gap-2 border-0 border-b border-solid border-[var(--inv-border-soft)] px-3 py-1.5 text-left',
                              active
                                ? 'bg-[var(--inv-accent-soft)]'
                                : 'bg-transparent hover:bg-[var(--inv-surface-muted)]',
                            )}
                            onClick={() =>
                              setSelectedFile(prev => {
                                const cached = accumulatedFiles.find(
                                  row =>
                                    (row.filePath || row.fileName) === path,
                                );
                                if (
                                  (prev?.filePath || prev?.fileName) === path &&
                                  prev?.abstractSyntaxTree
                                ) {
                                  return prev;
                                }
                                return cached?.abstractSyntaxTree
                                  ? {
                                      ...file,
                                      abstractSyntaxTree:
                                        cached.abstractSyntaxTree,
                                    }
                                  : file;
                              })
                            }>
                            <span className="min-w-0 truncate font-mono text-[0.75rem] text-[var(--inv-text)]">
                              {isMonorepo
                                ? stripProjectPrefix(path, project.id)
                                : path}
                            </span>
                            <span className="shrink-0 tabular-nums text-[0.7rem] text-[var(--inv-text-muted)]">
                              {file.totalLines}
                            </span>
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              ),
            )}
          </aside>

          <div className="flex min-h-0 min-w-0 flex-col gap-3 min-[960px]:h-full">
            {explorerFile ? (
              <>
                <InventoryCodePane
                  file={explorerFile}
                  repoUrl={selectedComponent.repoUrl}
                  branchName={selectedComponent.branchName}
                  scanId={selectedComponent.scanId}
                  pat={selectedComponent.pat}
                />
                <StructureInFile
                  fileName={explorerFile.fileName}
                  entities={structureEntities}
                />
              </>
            ) : (
              <p className="m-0 rounded-xl border border-solid border-[var(--inv-border)] px-4 py-8 text-sm text-[var(--inv-text-secondary)]">
                Select a file to inspect source and structure.
              </p>
            )}
          </div>
        </div>
      </section>

      <TechStackPanel technologies={selectedComponent.detectedTechnologies} />
    </div>
  );
};

export default ComponentInventoryView;
